import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import GoogleSignInButton from './GoogleSignInButton';
import pendingActionService from '../services/pendingActionService';
import SEO from './SEO';
import { PAGE_SEO } from '../utils/seoConfig';
import './AffiliateRegistration.css';

const AFFILIATE_TERMS = `
Terms and Conditions for FreshFitLab Affiliate Program

1. Program Overview
By participating in the FreshFitLab Affiliate Program ("Program"), you ("Affiliate") agree to be bound by these terms and conditions. FreshFitLab reserves the right to modify these terms at any time.

2. Eligibility
- Must be at least 18 years old
- Must have a valid email account
- Must comply with all applicable laws and regulations

3. Commission Structure
- Base commission rate starts at 2% of qualifying sales
- Commissions are calculated on the total sale amount before taxes and fees
- Commission rates may be adjusted based on performance
- Minimum payout threshold of $50

4. Payment Terms
- Payments are processed monthly for the previous month's earnings
- Payments are made via PayPal or approved payment method
- Affiliate is responsible for all applicable taxes
- Unclaimed payments expire after 12 months

5. Affiliate Obligations
- Accurately represent FreshFitLab's products and services
- Disclose affiliate relationship in compliance with FTC guidelines
- Maintain current and accurate contact and payment information
- Not engage in spam or deceptive marketing practices
- Not bid on FreshFitLab trademarks in paid advertising

6. Prohibited Activities
- Spamming or unsolicited email marketing
- False or misleading advertising
- Unauthorized use of FreshFitLab's intellectual property
- Creation of fake or fraudulent orders
- Manipulation of tracking systems or commission calculations

7. Content Guidelines
- All content must be original or properly licensed
- Content must not be offensive, illegal, or inappropriate
- FreshFitLab reserves the right to request content modifications
- Affiliate maintains ownership of their original content

8. Usage Limits
- Menu uploads are limited to 100 menus per affiliate account
- AI image generation is limited to 20 per day
- These limits may be adjusted based on account standing

9. Termination
- FreshFitLab reserves the right to suspend or terminate any affiliate relationship at any time
- Immediate suspension for violation of terms
- Final payment of earned commissions upon termination

10. Liability
- Affiliate operates as an independent contractor
- FreshFitLab is not liable for affiliate's business operations
- Affiliate agrees to indemnify FreshFitLab against claims

11. Privacy and Data
- Affiliate must comply with privacy laws and GDPR
- Maintain confidentiality of customer data
- Use tracking data only for Program purposes

12. Governing Law
These terms are governed by the laws of the jurisdiction where FreshFitLab is registered.

By accepting these terms, you acknowledge that you have read, understood, and agree to be bound by all the conditions outlined above.`;

const AffiliateRegistrationPreview = () => {
  const navigate = useNavigate();
  const [showTerms, setShowTerms] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [authError, setAuthError] = useState(null);

  const handleInteraction = (action) => {
    toast.error(`Sign in to ${action}`, {
      duration: 2000,
      icon: '🔒',
    });
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      if (!credentialResponse.credential) {
        setAuthError('Missing authentication credentials');
        return;
      }

      const apiUrl = `${process.env.REACT_APP_API_URL}/auth/google`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ 
          credential: credentialResponse.credential 
        }),
      });

      if (!response.ok) {
        throw new Error(`Authentication failed: ${response.status}`);
      }

      const data = await response.json();
      
      const userData = {
        ...data.user,
        sub: data.user.sub || data.user.id || data.user.email
      };
      
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', data.token);
      
      // Check for pending action after login (e.g., accept coach invite)
      const redirectUrl = await pendingActionService.checkAndExecutePendingAction();
      
      if (redirectUrl) {
        console.log('[AffiliateRegistrationPreview] Pending action executed, redirecting to:', redirectUrl);
        toast.success('Successfully connected with your coach!');
        // Use window.location.href to force full app reload with new auth state
        window.location.href = redirectUrl;
      } else {
        window.location.reload();
      }
      
    } catch (error) {
      console.error('Auth error:', error);
      setAuthError(`Login failed: ${error.message}`);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.error('Sign in to become a creator or coach', {
      duration: 3000,
      icon: '🔒',
    });
  };

  const seo = PAGE_SEO['/affiliate/register'];

  return (
    <div className="affiliate-registration-preview-wrapper">
      <SEO
        title={seo.title}
        description={seo.description}
        canonical="/affiliate/register"
      />
      <div className="preview-banner">
        <i className="fas fa-info-circle"></i>
        <span>Sign in with Google to join the affiliate program</span>
      </div>

      <div className="affiliate-registration preview-mode">
        <div className="registration-hero">
          <div className="hero-icon">
            <i className="fas fa-handshake"></i>
          </div>
          <h1>Become a Creator / Coach</h1>
          <p className="registration-intro">
            Join our creator program in seconds! Share recipes, build workout programs, and grow your audience.
          </p>
        </div>

        {/* Sign in prompt */}
        <div className="preview-signin-section">
          <h3>Sign in to get started</h3>
          <p>Create your affiliate account instantly with Google</p>
          <div className="preview-signin-buttons">
            {process.env.NODE_ENV === 'development' ? (
              <button 
                onClick={() => navigate('/dev/login')}
                className="dev-login-button"
              >
                <i className="fas fa-code"></i> Dev Login
              </button>
            ) : (
              <GoogleSignInButton
                onSuccess={handleGoogleSuccess}
                onError={() => setAuthError('Login Failed')}
                text="signin_with"
                size="large"
              />
            )}
          </div>
          {authError && <p className="auth-error">{authError}</p>}
        </div>

        <form onSubmit={handleSubmit} className="registration-form preview-form" noValidate>
          {/* Quick signup section - benefits */}
          <div className="quick-signup-section">
            <h3>Creator / Coach Benefits</h3>
            <div className="benefits-list">
              <div className="benefit-item">
                <i className="fas fa-check"></i>
                <span>Publish menus & recipes</span>
              </div>
              <div className="benefit-item">
                <i className="fas fa-check"></i>
                <span>Create workouts & training programs</span>
              </div>
              <div className="benefit-item">
                <i className="fas fa-check"></i>
                <span>Build a custom exercise library</span>
              </div>
              <div className="benefit-item">
                <i className="fas fa-check"></i>
                <span>Manage & train clients</span>
              </div>
              <div className="benefit-item">
                <i className="fas fa-check"></i>
                <span>Client nutrition & fitness plans</span>
              </div>
              <div className="benefit-item">
                <i className="fas fa-check"></i>
                <span>Collect payments via Stripe</span>
              </div>
              <div className="benefit-item">
                <i className="fas fa-check"></i>
                <span>Real-time analytics dashboard</span>
              </div>
              <div className="benefit-item">
                <i className="fas fa-link"></i>
                <span><a href="/pricing">View Creator / Coach Plans</a></span>
              </div>
            </div>
          </div>

          {/* Terms section */}
          <div className="form-section terms-section">
            <div className="terms-container">
              <button
                type="button"
                className="view-terms-button"
                onClick={() => setShowTerms(!showTerms)}
              >
                <i className={`fas fa-${showTerms ? 'chevron-up' : 'chevron-down'}`}></i>
                {showTerms ? 'Hide Terms' : 'View Terms and Conditions'}
              </button>
              
              {showTerms && (
                <div className="terms-content">
                  <pre>{AFFILIATE_TERMS}</pre>
                </div>
              )}
              
              <div className="terms-acceptance">
                <label className="checkbox-label preview-checkbox">
                  <input
                    type="checkbox"
                    name="acceptedTerms"
                    checked={acceptedTerms}
                    onChange={() => {
                      setAcceptedTerms(!acceptedTerms);
                      handleInteraction('accept terms');
                    }}
                    required
                  />
                  <span className="checkmark"></span>
                  I have read and agree to the Terms and Conditions
                </label>
              </div>
            </div>
          </div>

          {/* Optional fields toggle */}
          <div className="optional-fields-toggle">
            <button
              type="button"
              className="toggle-optional-button"
              onClick={() => setShowOptionalFields(!showOptionalFields)}
            >
              <i className={`fas fa-${showOptionalFields ? 'minus' : 'plus'}`}></i>
              {showOptionalFields ? 'Hide Optional Info' : 'Add Business Info (Optional)'}
            </button>
            <p className="optional-hint">You can add this information later from your dashboard</p>
          </div>

          {/* Optional fields preview */}
          {showOptionalFields && (
            <>
              <div className="form-section">
                <h2><i className="fas fa-building"></i> Business Information</h2>
                
                <div className="form-group">
                  <label htmlFor="businessName">Business Name</label>
                  <input
                    type="text"
                    id="businessName"
                    placeholder="Your business or brand name"
                    className="preview-input"
                    onClick={() => handleInteraction('enter business info')}
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="businessType">Business Type</label>
                  <select
                    id="businessType"
                    className="preview-input"
                    onClick={() => handleInteraction('select business type')}
                    defaultValue=""
                  >
                    <option value="">Select a type...</option>
                    <option value="BLOG">Blog</option>
                    <option value="SOCIAL_MEDIA">Social Media</option>
                    <option value="WEBSITE">Website</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="website">Website</label>
                  <input
                    type="url"
                    id="website"
                    placeholder="https://your-website.com"
                    className="preview-input"
                    onClick={() => handleInteraction('enter website')}
                    readOnly
                  />
                </div>
              </div>

              <div className="form-section">
                <h2><i className="fas fa-share-alt"></i> Social Media</h2>
                
                <div className="social-grid">
                  <div className="form-group">
                    <label htmlFor="instagram"><i className="fab fa-instagram"></i> Instagram</label>
                    <input
                      type="text"
                      id="instagram"
                      placeholder="@username"
                      className="preview-input"
                      onClick={() => handleInteraction('enter social media')}
                      readOnly
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="tiktok"><i className="fab fa-tiktok"></i> TikTok</label>
                    <input
                      type="text"
                      id="tiktok"
                      placeholder="@username"
                      className="preview-input"
                      onClick={() => handleInteraction('enter social media')}
                      readOnly
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="facebook"><i className="fab fa-facebook"></i> Facebook</label>
                    <input
                      type="text"
                      id="facebook"
                      placeholder="Page name or URL"
                      className="preview-input"
                      onClick={() => handleInteraction('enter social media')}
                      readOnly
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="twitter"><i className="fab fa-twitter"></i> Twitter/X</label>
                    <input
                      type="text"
                      id="twitter"
                      placeholder="@username"
                      className="preview-input"
                      onClick={() => handleInteraction('enter social media')}
                      readOnly
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h2><i className="fas fa-credit-card"></i> Payment Information</h2>
                <p className="section-hint">Required to receive commission payments</p>
                
                <div className="form-group">
                  <label htmlFor="paymentEmail">PayPal Email</label>
                  <input
                    type="email"
                    id="paymentEmail"
                    placeholder="your-paypal@email.com"
                    className="preview-input"
                    onClick={() => handleInteraction('enter payment info')}
                    readOnly
                  />
                </div>
              </div>
            </>
          )}

          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-button preview-submit-btn"
              disabled
            >
              <i className="fas fa-rocket"></i> Start as Creator/Coach
            </button>
            <p className="form-hint">Sign in above to create your affiliate account</p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AffiliateRegistrationPreview;
